import { buildSiteHierarchyGroups, type SiteTreeMeta } from '@/lib/catalogSiteTree';

export type SiteWecomGroupRow = {
  enabled: boolean;
  entry_mode: 'plugin' | 'webview' | 'copy' | 'image';
  button_text: string;
  title: string;
  subtitle: string;
  entry_url: string;
  icon_image_url: string;
  qr_image_url: string;
};

export type MiniproWecomSite = SiteTreeMeta & {
  site_code?: string;
  enabled?: boolean;
};

const DEFAULT_WECOM: SiteWecomGroupRow = {
  enabled: false,
  entry_mode: 'plugin',
  button_text: '立即入群',
  title: '企业微信群',
  subtitle: '',
  entry_url: '',
  icon_image_url: '',
  qr_image_url: '',
};

/** 展示站点勾选 id 展开其下二级（与 catalog 口径一致） */
export function expandMiniproSiteIds(siteIds: number[], allSites: MiniproWecomSite[]): Set<number> {
  const byParent = new Map<number, MiniproWecomSite[]>();
  for (const s of allSites) {
    const pid = s.parent_site_id != null && s.parent_site_id > 0 ? s.parent_site_id : 0;
    if (pid > 0) {
      const list = byParent.get(pid) ?? [];
      list.push(s);
      byParent.set(pid, list);
    }
  }
  const acc = new Set<number>();
  for (const id of siteIds) {
    if (id > 0) {
      acc.add(id);
    }
  }
  let changed = true;
  while (changed) {
    changed = false;
    for (const id of [...acc]) {
      const kids = byParent.get(id) ?? [];
      for (const c of kids) {
        if (!acc.has(c.id)) {
          acc.add(c.id);
          changed = true;
        }
      }
    }
  }
  return acc;
}

export function wecomSitesInScope(siteIds: number[], allSites: MiniproWecomSite[]): MiniproWecomSite[] {
  const scope = expandMiniproSiteIds(siteIds, allSites);
  return allSites.filter((s) => scope.has(s.id));
}

export function hasWecomOverride(groups: Record<string, SiteWecomGroupRow>, siteId: number): boolean {
  return Object.prototype.hasOwnProperty.call(groups, String(siteId));
}

function normalizeRow(raw: Partial<SiteWecomGroupRow> | undefined): SiteWecomGroupRow {
  if (!raw) {
    return { ...DEFAULT_WECOM };
  }
  const entryMode =
    raw.entry_mode === 'webview' || raw.entry_mode === 'copy' || raw.entry_mode === 'image'
      ? raw.entry_mode
      : 'plugin';
  return {
    enabled: !!raw.enabled,
    entry_mode: entryMode,
    button_text: raw.button_text || (entryMode === 'plugin' ? '立即入群' : entryMode === 'image' ? '进群' : '进群入口'),
    title: raw.title || '企业微信群',
    subtitle: raw.subtitle || '',
    entry_url: raw.entry_url || '',
    icon_image_url: raw.icon_image_url || '',
    qr_image_url: raw.qr_image_url || '',
  };
}

export function mergeWecomCfg(parent: SiteWecomGroupRow, own: SiteWecomGroupRow): SiteWecomGroupRow {
  const merged: SiteWecomGroupRow = { ...parent };
  merged.enabled = own.enabled;
  if (own.entry_mode) {
    merged.entry_mode = own.entry_mode;
  }
  for (const k of ['button_text', 'title', 'subtitle', 'entry_url', 'icon_image_url', 'qr_image_url'] as const) {
    const v = own[k]?.trim() ?? '';
    if (v !== '') {
      merged[k] = own[k];
    }
  }
  return merged;
}

/** 无单独配置时沿用一级；有单独配置时字段级覆盖一级 */
export function effectiveWecomCfg(
  groups: Record<string, SiteWecomGroupRow>,
  siteId: number,
  sitesById: Map<number, MiniproWecomSite>,
): SiteWecomGroupRow {
  const site = sitesById.get(siteId);
  const ownRaw = groups[String(siteId)];
  const pid = site?.parent_site_id != null && site.parent_site_id > 0 ? site.parent_site_id : 0;
  if (pid > 0) {
    const parentEff = effectiveWecomCfg(groups, pid, sitesById);
    if (!hasWecomOverride(groups, siteId)) {
      return parentEff;
    }
    return mergeWecomCfg(parentEff, normalizeRow(ownRaw));
  }
  return normalizeRow(ownRaw);
}

export function wecomEnabledSummary(cfg: SiteWecomGroupRow): string {
  if (!cfg.enabled) {
    return '未开启';
  }
  const mode =
    cfg.entry_mode === 'image' ? '图片' : cfg.entry_mode === 'plugin' ? '插件' : cfg.entry_mode === 'copy' ? '复制' : 'H5';
  return `已开启 · ${mode}`;
}

export function buildWecomHierarchyGroups(siteIds: number[], allSites: MiniproWecomSite[]) {
  return buildSiteHierarchyGroups(wecomSitesInScope(siteIds, allSites));
}
